import { useQuery } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { OBLIGATION_STATUS_LABELS, type PublicAssetDto } from "@klantpunt/shared";
import { BrandMark } from "../app/BrandMark";
import { IntakeForm } from "../features/intake/IntakeForm";
import { api, ApiError } from "../lib/api";
import { can, hasModule, useMe } from "../lib/auth";

interface QrHit {
  assetId: string;
  buildingId: string;
  code: string;
  name: string;
}

const fmtDate = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString("nl-NL", { dateStyle: "medium" });

/**
 * Link uit een QR-label (/q/<token>). Ingelogd met recht op bevindingen: direct naar de asset. Anders de
 * openbare kaart: asset, keuringen en, als het gebouw dat toestaat, **Probleem melden** (docs/MELDINGEN.md §3).
 * Een ingelogde gebruiker zonder recht op bevindingen (bv. een klant) meldt hier via de app.
 */
export function QrPage() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const { data: me, isPending: mePending } = useMe();
  const inApp = Boolean(me?.org);
  const direct = inApp && can(me, "issue.create") && hasModule(me, "assets");
  const hit = useQuery({
    queryKey: ["qr", token],
    queryFn: () => api<QrHit>(`/assets/by-qr?code=${encodeURIComponent(token)}`),
    enabled: inApp && hasModule(me, "assets"),
    retry: false,
  });
  const pub = useQuery({
    queryKey: ["qr-public", token],
    queryFn: () => api<PublicAssetDto>(`/public/q/${encodeURIComponent(token)}`),
    enabled: !mePending && (!direct || hit.isError),
    retry: false,
  });
  const [mode, setMode] = useState<"card" | "form" | { code: string | null }>("card");

  useEffect(() => {
    if (direct && hit.data) navigate(`/gebouwen/${hit.data.buildingId}?asset=${hit.data.assetId}`, { replace: true });
  }, [direct, hit.data, navigate]);

  // Ingelogd in dezelfde organisatie: melden via de app (kanaal App), los van de schakelaar voor QR.
  const appAsset = inApp && !direct && hit.data ? hit.data : null;
  const canReport = Boolean(appAsset) || Boolean(pub.data?.canReport);
  const url = appAsset
    ? `/api/assets/${appAsset.assetId}/intake`
    : `/api/public/q/${encodeURIComponent(token)}/reports`;

  const shell = (body: ReactNode) => (
    <div className="qrpublic">
      <header className="qrhead">
        <BrandMark />
        {!me && (
          <Link to={`/inloggen?terug=${encodeURIComponent(`/q/${token}`)}`} className="btn ghost small">
            Inloggen
          </Link>
        )}
      </header>
      <main className="page narrow">{body}</main>
    </div>
  );

  if (mePending || (direct && !hit.isError) || pub.isPending)
    return shell(
      <p className="muted" role="status">
        Asset zoeken…
      </p>,
    );
  if (pub.isError && !appAsset)
    return shell(
      <>
        <h1 className="dot">QR-code</h1>
        <div className="alert" role="alert">
          {pub.error instanceof ApiError ? pub.error.message : "Opzoeken mislukt"}
        </div>
        {inApp && (
          <Link to="/scan" className="btn">
            Andere code scannen
          </Link>
        )}
      </>,
    );

  const d = pub.data;
  const name = appAsset?.name ?? d?.asset.name ?? "";
  const code = appAsset?.code ?? d?.asset.code ?? "";

  if (typeof mode === "object")
    return shell(
      <section className="panel stack" aria-live="polite">
        <h1 className="dot">Bedankt voor je melding</h1>
        <p>
          {mode.code ? (
            <>
              Je melding <b className="code">{mode.code}</b> over {name} is ontvangen.
            </>
          ) : (
            <>Je melding over {name} is ontvangen.</>
          )}{" "}
          De servicedesk bekijkt hem zo snel mogelijk.
        </p>
        <div className="btnrow">
          <button type="button" className="btn" onClick={() => setMode("card")}>
            Terug naar de asset
          </button>
        </div>
      </section>,
    );

  return shell(
    <>
      <p className="label">
        {d ? `${d.org.name} · ${d.building.name}${d.building.city ? `, ${d.building.city}` : ""}` : "Asset"}
      </p>
      <h1 className="dot">{name}</h1>
      <p className="muted small code">{code}</p>

      {mode === "form" ? (
        <section className="panel" aria-label="Probleem melden">
          <h2>Probleem melden</h2>
          <IntakeForm
            url={url}
            publicForm={!inApp}
            onCancel={() => setMode("card")}
            onDone={(c) => setMode({ code: c })}
          />
        </section>
      ) : (
        <>
          {d && (
            <section className="panel" aria-labelledby="qr-keur">
              <h2 id="qr-keur">Onderhoud en keuringen</h2>
              {d.obligations.length ? (
                <ul className="qrobls">
                  {d.obligations.map((o, i) => (
                    <li key={i}>
                      <i className={`obdot ob-${o.status}`} aria-hidden="true" />
                      <span className="grow">{o.name}</span>
                      <span className="muted small">
                        {OBLIGATION_STATUS_LABELS[o.status]}
                        {o.nextDueOn && ` · volgende ${fmtDate(o.nextDueOn)}`}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">Voor deze asset zijn geen keuringen vastgelegd.</p>
              )}
            </section>
          )}
          <div className="btnrow qractions">
            {canReport && (
              <button type="button" className="btn primary" onClick={() => setMode("form")}>
                Probleem melden
              </button>
            )}
            {appAsset && hasModule(me, "assets") && (
              <Link to={`/gebouwen/${appAsset.buildingId}?asset=${appAsset.assetId}`} className="btn">
                Asset openen
              </Link>
            )}
          </div>
          {!canReport && !inApp && (
            <p className="muted small">Melden via deze QR-code staat uit. Neem contact op met de beheerder.</p>
          )}
        </>
      )}
    </>,
  );
}
